import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';

import { ObjectChange } from '@lib/shared-ui/core/utils/generate-object-changes.function';
import { LiloDrugSearchResponse, SubstanceQuantity } from '@lib/core/models/protocol.interface';

import { GuaranteeApplicationStatus, GuaranteeStatus } from './guarantee-application.interface';

export interface PrescriptionsSearch {
  page: number;
  pageSize: number;
  sortField?: string; // default sorting by creationDate, other options should
  sortOrder?: 'Ascending' | 'Descending';
  userId?: string;
  guaranteeApplicationId?: string;
  patientName?: string;
  prescriptionName?: string;
  prescriptionType?: PrescriptionTypes;
  planningStatus?: PrescriptionPlanningStatus;
  guaranteeStatus?: GuaranteeStatus;
  historyStatus?: PrescriptionHistoryStatus;
  guaranteeApplicationStatus?: GuaranteeApplicationStatus;
  startDate?: string;
  endDate?: string;
  creatorUserId?: string;
  hasPlanning?: boolean;
  medicalDataArrived?: boolean;
}

export interface Prescription {
  id: string;
  type: PrescriptionTypes;
  originalPrescriptionId: string;
  name: string;
  guaranteeStatus: GuaranteeStatus;
  historyStatus: PrescriptionHistoryStatus;
  planningStatus: PrescriptionPlanningStatus;
  startDate: string;
  endDate: string;
  metadata: PharmacologicalItemMetaData | ServiceMetadata | PersonalDoctorMetadata;
  creationDate: string;
  lastUpdateByName: string;
  lastUpdateByUserId: string;
  lastUpdateDate: string;
  medicalDataArrived: boolean;
  medicalDataSourceType: number;
  reason: string;
  historicalChanges: ObjectChange<Prescription>[];
  sendGuaranteeNotification: boolean;
  status?: PrescriptionStatus;
  creatorUserId?: string;
  creatorName?: string;
  count?: number;
  used?: number;
  instructions?: [];
  planning?: PrescriptionPlanning[];
  activityId?: string;
  guaranteeApplicationId?: string;
  userId?: string;
  franchise?: number;
  insuranceReservePrice: number;
  completedInAppointment?: boolean;
  completionReason?: string;
  declined?: number;
}

export interface PrescriptionPlanning {
  $type?: string; // TODO: validate that this field is correct for FE and doesn't need changes on BE
  id: string;
  planningStatus: PrescriptionPlanningStatus;
  actionId: string;
  dateStart: Date;
  dateEnd: Date;
  orderId?: string;
  orderNumber?: string;
  appointmentId?: string;
  doctorId?: string;
  doctorName?: string;
  integrationType?: IntegrationType;
  partnerId?: string;
  partnerName?: string;
  locationId?: string;
  locationAddress?: string;
  lastUpdateDate?: Date;
}

// TODO: interface is duplicated. Need to be moved or refactored to avoid duplicates
export enum IntegrationType {
  None = 'None',
  Helsi = 'Helsi',
  Eleks = 'Eleks',
  Lilo = 'Lilo',
  Dila = 'Dila',
  Pharmacy911 = 'Pharmacy911',
  PharmacyAdd = 'PharmacyAdd',
  PharmacyDobrobut = 'PharmacyDobrobut',
}

export enum PrescriptionHistoryStatus {
  Active = 'Active',
  Archived = 'Archived',
}

export enum PrescriptionPlanningStatus {
  NotPlanned = 'NotPlanned',
  Planned = 'Planned',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
  WillNotDo = 'WillNotDo',
  Failed = 'Failed',
  PendingCreation = 'PendingCreation',
  PendingReschedule = 'PendingReschedule',
  PendingCancellation = 'PendingCancellation',
  NoShow = 'NoShow',
}

export interface ResponseMorionItem {
  Code: number;
  QuantityInPackage: number;
  NameUa: string;
  NameFullUa: string;
  FormUa: string;
  SubsctanceQuantity: number;
  SubstanceQuantities: SubstanceQuantity[] | null;
  HasReceipt: boolean;
}

export interface ServiceMetadata {
  code: string;
  quantity: string;
}

export interface PrescriptionsListItem {
  id: string;
  type: PrescriptionTypes;
  name: string;
  status: PrescriptionStatus;
  metadata: PharmacologicalItemMetaData;
  score?: number;
  count: number;
  used: number;
  serviceCode?: string;
  franchise?: number;
}

export interface AppointmentServiceCode {
  code: string;
  name: string;
}

export enum PrescriptionTypes {
  Drugs = 'Drugs',
  InstrumentalDiagnostic = 'InstrumentalDiagnostic',
  LaboratoryService = 'LaboratoryService',
  RelatedSpecialists = 'Appointment',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  Appointment = 'Appointment',
  PersonalDoctorAppointment = 'PersonalDoctorAppointment',
}

export enum FranchisePrescriptionType {
  Achi = 'Achi',
  Drugs = 'Drugs',
  LaboratoryService = 'LaboratoryService',
  Unknown = 'Unknown',
}

export interface MedicalServiceRequestBody {
  description: string;
  prescriptions: PrescriptionRequestBody[];
  appointmentId: string;
  status?: GuaranteeApplicationStatus;
  sendGuaranteeNotification: boolean;
}

export interface PrescriptionRequestBody {
  type: PrescriptionTypes;
  name: string;
  startDate?: Date;
  endDate?: Date;
  metadata: PharmacologicalItemMetaData | ServiceItemMetadata | PersonalDoctorMetadata;
  franchise: number;
}

export interface PharmacologicalItemMetaData {
  comment?: string;
  daily_amount?: string;
  duration?: string;
  form?: string;
  frequency?: string;
  inn?: string;
  is_pharmalogical?: string;
  morion_items?: string;
  trademarks?: string;
  morion_substance?: string;
  inn_ua?: string;
  single_dose?: string;
  treatment_dose?: string;
  treatment_packages?: string;
  rejectionReason?: string;
}

export interface ServiceItemMetadata {
  quantity: string;
  code: string;
  reason?: string;
}

export interface PersonalDoctorMetadata {
  doctor_name?: string;
  service_id: string;
  doctor_id: string;
  doctor_partner_id?: string;
  reason?: string;
}

export enum PrescriptionStatus {
  Auto = 'Auto',
  Guaranteed = 'Guaranteed',
  NotGuaranteed = 'NotGuaranteed',
  WaitProcessing = 'WaitProcessing',
  Unknown = 'Unknown',
  Planned = 'Planned',
  Completed = 'Completed',
  Archived = 'Archived',
  WillNotDo = 'WillNotDo',
}

export interface PrescriptionItem<T> {
  id?: string;
  type: PrescriptionTypes;
  name: string;
  startDate?: Date;
  endDate?: Date;
  metadata: T;
  guaranteeStatus?: string;
  reason?: string;
  sendGuaranteeNotification: boolean;
  franchise: number;
  completedInAppointment: boolean;
  completionReason: string | null;
  guaranteeId: string | null;
  originalPrescriptionId: string;
}

export interface CreatedMedicalServiceValue {
  description: string;
  appointmentType: number;
  appointmentId: string;
  sendGuaranteeNotification: boolean;
}

export enum AppointmentsTypes {
  First = 1,
  Completed = 2,
}

export interface MedicalServiceFormControls {
  description: FormControl<string | null>;
  appointmentType: FormControl<number | null>;
  appointmentId: FormControl<string | null>;
  sendGuaranteeNotification: FormControl<boolean>;
}

export interface PrescriptionControls<T extends { [K in keyof T]: AbstractControl }> {
  type: FormControl<PrescriptionTypes | null>;
  name: FormControl<string | null>;
  startDate?: FormControl<Date | null>;
  endDate?: FormControl<Date | null>;
  metadata: FormGroup<T>;
  guaranteeStatus?: FormControl<string | null>;
  editReason?: FormControl<string | null>;
  statusReason?: FormControl<string | null>;
  sendGuaranteeNotification: FormControl<boolean | null>;
  franchise: FormControl<number>;
  franchiseManualChangeConfirmation: FormControl<boolean>;
  completedInAppointment?: FormControl<boolean>;
  completionReason?: FormControl<string | null>;
  originalPrescriptionId: FormControl<string | null>;
}

export interface PharmacologicalTreatmentControls {
  form: FormControl<string[] | null>;
  singleDose: FormControl<number | null>;
  frequency: FormControl<string | null>;
  treatmentDose: FormControl<number | null>;
  treatmentDoseConfirmation: FormControl<boolean | null>;
  dailyAmount: FormControl<number | null>;
  duration: FormControl<number | null>;
  inn: FormControl<string | null>;
  morionItems: FormArray<FormControl<LiloDrugSearchResponse | null>>;
  morionSubstance: FormControl<string | null>;
}

export interface GenericServiceControls {
  code: FormControl<string | null>;
  quantity: FormControl<number | null>;
}

export interface PersonalDoctorControls {
  serviceId: FormControl<string | null>;
  serviceName: FormControl<string | null>;
  doctorId: FormControl<string | null>;
  doctorName: FormControl<string | null>;
  doctorPartnerId: FormControl<string | null>;
}

export interface PrescriptionFormValue<T> {
  id: string | null;
  guaranteeId: string | null;
  type: PrescriptionTypes | null;
  name: string | null;
  startDate?: Date | null;
  endDate?: Date | null;
  metadata: T;
  guaranteeStatus?: string | null;
  reason?: string | null;
  statusReason?: string | null;
  sendGuaranteeNotification: boolean | null;
  franchise: number;
  franchiseManualChangeConfirmation: boolean;
  completedInAppointment?: boolean;
  completionReason?: string | null;
  originalPrescriptionId: string;
}

export interface PharmacologicalTreatmentValue {
  form: string[] | null;
  singleDose: number | null;
  frequency: string | null;
  treatmentDose: number | null;
  treatmentDoseConfirmation: boolean | null;
  dailyAmount: number | null;
  duration: number | null;
  inn: string | null;
  morionItems: (LiloDrugSearchResponse | null)[];
  morionSubstance: string | null;
}

export interface GenericServiceValue {
  code: string | null;
  quantity: number | null;
}

export interface PersonalDoctorValue {
  serviceId: string | null;
  serviceName: string | null;
  doctorId: string | null;
  doctorName: string | null;
  doctorPartnerId: string | null;
}

export interface DrugsFrequency {
  id_drugs_frequency: number;
  frequency: string;
  active: number;
  value: string;
  sort: string;
}

export const DRUGS_FREQUENCY: DrugsFrequency[] = [
  {
    id_drugs_frequency: 5,
    frequency: 'Щогодини',
    active: 1,
    value: '24',
    sort: '24',
  },
  {
    id_drugs_frequency: 23,
    frequency: '8 разів на добу',
    active: 1,
    value: '8',
    sort: '8',
  },
  {
    id_drugs_frequency: 6,
    frequency: '6 разів на добу',
    active: 1,
    value: '6',
    sort: '6',
  },
  {
    id_drugs_frequency: 11,
    frequency: '5 разів на добу',
    active: 1,
    value: '5',
    sort: '5',
  },
  {
    id_drugs_frequency: 4,
    frequency: '4 рази на добу',
    active: 1,
    value: '4',
    sort: '4',
  },
  {
    id_drugs_frequency: 3,
    frequency: '3 рази на добу',
    active: 1,
    value: '3',
    sort: '3',
  },
  {
    id_drugs_frequency: 2,
    frequency: '2 рази на добу',
    active: 1,
    value: '2',
    sort: '2',
  },
  {
    id_drugs_frequency: 1,
    frequency: '1 раз на добу',
    active: 1,
    value: '1',
    sort: '1',
  },
  {
    id_drugs_frequency: 24,
    frequency: '5 днів прийом, 2 дні перерва',
    active: 1,
    value: '0.71',
    sort: '0.71',
  },
  {
    id_drugs_frequency: 10,
    frequency: 'Через день',
    active: 1,
    value: '0.5',
    sort: '0.5',
  },
  {
    id_drugs_frequency: 26,
    frequency: '3 рази на тиждень',
    active: 1,
    value: '0.43',
    sort: '0.43',
  },
  {
    id_drugs_frequency: 12,
    frequency: '1 раз на 3 дні',
    active: 1,
    value: '0.33',
    sort: '0.33',
  },
  {
    id_drugs_frequency: 25,
    frequency: '2 рази на тиждень',
    active: 1,
    value: '0.28',
    sort: '0.28',
  },
  {
    id_drugs_frequency: 16,
    frequency: '1 раз на 7 днів',
    active: 1,
    value: '0.14',
    sort: '0.14',
  },
  {
    id_drugs_frequency: 17,
    frequency: '1 раз на 10 днів',
    active: 1,
    value: '0.1',
    sort: '0.1',
  },
  {
    id_drugs_frequency: 18,
    frequency: '1 раз на 14 днів',
    active: 1,
    value: '0.07',
    sort: '0.07',
  },
  {
    id_drugs_frequency: 19,
    frequency: '1 раз на 21 день',
    active: 1,
    value: '0.05',
    sort: '0.05',
  },
  {
    id_drugs_frequency: 21,
    frequency: '1 раз на 28 днів',
    active: 1,
    value: '0.035',
    sort: '0.035',
  },
  {
    id_drugs_frequency: 20,
    frequency: '1 раз на місяць',
    active: 1,
    value: '0.03',
    sort: '0.03',
  },
  {
    id_drugs_frequency: 22,
    frequency: '1 раз на 3 місяці',
    active: 1,
    value: '0.01',
    sort: '0.01',
  },
];
